import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Plus, Trash2, Pencil, Rocket, Loader2, ArrowRightLeft } from 'lucide-react';
import { useStrategyLabStore } from '../../stores/strategyLabStore';
import type { Strategy } from '../../types/strategyLab';
import StrategyBuilder from '../../components/strategy-lab/StrategyBuilder';
import DeployStrategyDialog from '../../components/strategy-lab/DeployStrategyDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const SIDE_TONE: Record<string, string> = {
  long: 'text-emerald-500',
  short: 'text-rose-500',
};

const StrategiesPage: React.FC = () => {
  const navigate = useNavigate();
  const strategies = useStrategyLabStore((s) => s.strategies);
  const loading = useStrategyLabStore((s) => s.strategiesLoading);
  const error = useStrategyLabStore((s) => s.strategiesError);
  const fetchStrategies = useStrategyLabStore((s) => s.fetchStrategies);
  const deleteStrategy = useStrategyLabStore((s) => s.deleteStrategy);
  const scanners = useStrategyLabStore((s) => s.scanners);
  const fetchScanners = useStrategyLabStore((s) => s.fetchScanners);

  const [mode, setMode] = useState<'idle' | 'create' | 'edit'>('idle');
  const [editing, setEditing] = useState<Strategy | null>(null);
  const [deployTarget, setDeployTarget] = useState<Strategy | null>(null);
  const [deployOpen, setDeployOpen] = useState(false);

  useEffect(() => {
    fetchStrategies();
    if (scanners.length === 0) fetchScanners();
  }, [fetchStrategies, fetchScanners, scanners.length]);

  const startCreate = () => {
    setEditing(null);
    setMode('create');
  };
  const startEdit = (str: Strategy) => {
    setEditing(str);
    setMode('edit');
  };
  const finishBuilder = () => {
    setMode('idle');
    setEditing(null);
  };
  const openDeploy = (str: Strategy) => {
    setDeployTarget(str);
    setDeployOpen(true);
  };

  const scannerName = (id: string) =>
    scanners.find((s) => s.id === id)?.name ?? 'Unknown scanner';

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.3fr]">
      {/* List column */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" /> Strategies
          </h2>
          <Button size="sm" onClick={startCreate}>
            <Plus className="w-4 h-4" /> New
          </Button>
        </div>

        {error && <p className="text-sm text-rose-500">{error}</p>}

        {loading && strategies.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading strategies…
          </div>
        ) : strategies.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground text-sm">
              No strategies yet. Build one over a scanner, then deploy it with a budget.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {strategies.map((str) => (
              <Card
                key={str.id}
                className={`transition-colors ${editing?.id === str.id ? 'border-primary' : ''}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="text-base truncate">{str.name}</CardTitle>
                      <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                        <ArrowRightLeft className="w-3 h-3" />
                        {str.scanner_name ?? scannerName(str.scanner_id)}
                      </p>
                    </div>
                    <Badge
                      variant="secondary"
                      className={`shrink-0 uppercase ${SIDE_TONE[str.side] ?? ''}`}
                    >
                      {str.side}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => openDeploy(str)}>
                    <Rocket className="w-3.5 h-3.5" /> Deploy
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => startEdit(str)}>
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-rose-500 hover:text-rose-600"
                    onClick={() => {
                      if (window.confirm(`Delete strategy “${str.name}”?`)) {
                        deleteStrategy(str.id);
                        if (editing?.id === str.id) finishBuilder();
                      }
                    }}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Builder column */}
      <div>
        {mode === 'idle' ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center text-muted-foreground text-sm space-y-3">
              <p>Select a strategy to edit, or craft a new one over one of your scanners.</p>
              <Button variant="outline" size="sm" onClick={startCreate}>
                <Plus className="w-4 h-4" /> New strategy
              </Button>
            </CardContent>
          </Card>
        ) : (
          <StrategyBuilder editing={mode === 'edit' ? editing : null} onDone={finishBuilder} />
        )}
      </div>

      <DeployStrategyDialog
        strategy={deployTarget}
        open={deployOpen}
        onOpenChange={setDeployOpen}
        onDeployed={() => navigate('/strategy-lab/deployments')}
      />
    </div>
  );
};

export default StrategiesPage;
